// components/dashboard/PinDisplay.jsx
import React, { useState, useRef, useEffect } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const PinDisplay = ({ pin, reservation }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [checkInPhoto, setCheckInPhoto] = useState(reservation?.checkInPhoto || null);
  const [checkOutPhoto, setCheckOutPhoto] = useState(reservation?.checkOutPhoto || null);
  const [uploading, setUploading] = useState({ checkIn: false, checkOut: false });
  const [photoError, setPhotoError] = useState(null);
  
  const checkInInputRef = useRef(null);
  const checkOutInputRef = useRef(null);

  // Reservation değiştiğinde fotoğrafları güncelle
  useEffect(() => {
    setCheckInPhoto(reservation?.checkInPhoto || null);
    setCheckOutPhoto(reservation?.checkOutPhoto || null);
  }, [reservation?.checkInPhoto, reservation?.checkOutPhoto]);
  
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // Kapı kodunun aktif olup olmadığını kontrol et
  const isCodeActive = () => {
    if (!reservation) return false;
    const now = new Date();
    
    let startsAt = null;
    if (reservation.pinStartsAt?.toDate) {
      startsAt = reservation.pinStartsAt.toDate();
    } else if (reservation.pinStartsAt) {
      startsAt = new Date(reservation.pinStartsAt);
    }
    
    let endsAt = null;
    if (reservation.pinEndsAt?.toDate) {
      endsAt = reservation.pinEndsAt.toDate();
    } else if (reservation.pinEndsAt) {
      endsAt = new Date(reservation.pinEndsAt);
    }
    
    if (startsAt && endsAt) {
      return now >= startsAt && now <= endsAt;
    }
    return !!pin;
  };

  // Kod süresi bitmiş mi?
  const isCodeExpired = () => {
    if (!reservation) return false;
    const now = new Date();
    
    let endsAt = null;
    if (reservation.pinEndsAt?.toDate) {
      endsAt = reservation.pinEndsAt.toDate();
    } else if (reservation.pinEndsAt) {
      endsAt = new Date(reservation.pinEndsAt);
    }
    
    if (endsAt) {
      return now > endsAt;
    }
    return false;
  };

  // Kod aktif veya aktif olmuştu mu? (giriş fotoğrafı için)
  const canTakeCheckInPhoto = () => {
    return isCodeActive() || isCodeExpired();
  };

  // Çıkış fotoğrafı çekilebilir mi?
  // Giriş fotoğrafı çekildiyse her zaman çıkış fotoğrafı çekilebilir (erken çıkışa izin ver)
  const canTakeCheckOutPhoto = () => {
    return checkInPhoto && (isCodeActive() || isCodeExpired());
  };

  // Zaman formatı
  const formatTime = (dateValue) => {
    if (!dateValue) return '';
    
    let date;
    if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    
    return date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) || '';
  };

  // Fotoğraf yükleme
  const handlePhotoCapture = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file || !reservation) return;

    setPhotoError(null);
    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Fotoğraf boyutu 5MB\'dan küçük olmalıdır');
      }

      const timestamp = Date.now();
      const fileName = `reservations/${reservation.id}/${type}_${timestamp}.jpg`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const savePhotoCallable = httpsCallable(functions, 'saveCheckInOutPhoto');
      await savePhotoCallable({
        reservationId: reservation.id,
        type: type,
        photoUrl: downloadURL
      });

      if (type === 'checkIn') {
        setCheckInPhoto(downloadURL);
      } else {
        setCheckOutPhoto(downloadURL);
      }

    } catch (err) {
      console.error('Fotoğraf yükleme hatası:', err);
      setPhotoError(err.message || 'Fotoğraf yüklenirken bir hata oluştu');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };
  
  return (
    <Card title="Kapı Giriş Kodu" className="card fade-in">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {pin ? (
            <>
              <p className="text-sm text-blue-200 mb-3">
                Bugünkü rezervasyonunuz için kapı giriş kodunuz:
              </p>
              <div className={`${isCodeActive() ? 'bg-green-900 border-green-500' : isCodeExpired() ? 'bg-red-900 border-red-500' : 'bg-blue-900 border-blue-500'} bg-opacity-40 border py-4 px-6 rounded-lg mb-3 text-center relative overflow-hidden glass-effect`}>
                <div className={`absolute inset-0 ${isCodeActive() ? 'bg-gradient-to-r from-green-600 to-emerald-600' : isCodeExpired() ? 'bg-gradient-to-r from-red-600 to-rose-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'} opacity-20`}></div>
                <span className={`text-3xl font-mono font-bold tracking-widest pin-display ${isVisible ? 'text-blue-100' : 'text-blue-100'}`} 
                      style={{ textShadow: isVisible ? '0 0 15px rgba(59, 130, 246, 0.7)' : 'none'}}>
                  {isVisible ? pin : '••••••'}
                </span>
                {isVisible && <div className="pulse-animation absolute inset-0 rounded-lg pointer-events-none"></div>}
              </div>
              
              {/* Kod geçerlilik zamanı */}
              {reservation && (reservation.pinStartsAt || reservation.startTime) && (
                <div className="flex items-center justify-center gap-2 mb-3 text-sm">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full ${
                    isCodeActive() 
                      ? 'bg-green-800 text-green-200' 
                      : isCodeExpired() 
                        ? 'bg-red-800 text-red-200'
                        : 'bg-yellow-800 text-yellow-200'
                  }`}>
                    {isCodeActive() ? (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Kod Aktif
                      </>
                    ) : isCodeExpired() ? (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Süre Doldu
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Bekliyor
                      </>
                    )}
                  </span>
                  <span className="text-gray-400">
                    {reservation.startTime || formatTime(reservation.pinStartsAt)} - {reservation.endTime || formatTime(reservation.pinEndsAt)}
                  </span>
                </div>
              )}

              {/* Fotoğraf Çekme Bölümü */}
              {reservation && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-semibold text-blue-200 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Giriş/Çıkış Fotoğrafları
                  </h4>

                  {photoError && (
                    <div className="mb-3 p-2 bg-red-900/50 border border-red-500 rounded text-red-200 text-xs">
                      {photoError}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {/* Giriş Fotoğrafı - Kod aktifken veya süre dolduktan sonra */}
                    <div className={`rounded-lg p-3 border ${canTakeCheckInPhoto() && !checkInPhoto ? 'bg-green-900/30 border-green-600' : 'bg-gray-800/50 border-gray-700'}`}>
                      <p className="text-xs font-medium text-green-300 mb-2 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                        </svg>
                        Giriş
                      </p>
                      
                      {checkInPhoto ? (
                        <div className="relative">
                          <img 
                            src={checkInPhoto} 
                            alt="Giriş" 
                            className="w-full h-20 object-cover rounded"
                          />
                          <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                            ✓
                          </div>
                        </div>
                      ) : canTakeCheckInPhoto() ? (
                        <>
                          <input
                            ref={checkInInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handlePhotoCapture(e, 'checkIn')}
                            className="hidden"
                          />
                          <button
                            onClick={() => checkInInputRef.current?.click()}
                            disabled={uploading.checkIn}
                            className="w-full py-3 rounded border-2 border-dashed border-green-500 hover:bg-green-900/30 transition-all"
                          >
                            {uploading.checkIn ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500 mx-auto"></div>
                            ) : (
                              <div className="text-center">
                                <svg className="w-6 h-6 mx-auto text-green-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-xs text-green-400">Çek</span>
                              </div>
                            )}
                          </button>
                        </>
                      ) : (
                        <div className="py-3 text-center text-gray-500">
                          <svg className="w-6 h-6 mx-auto opacity-50 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-xs">Kod aktif olunca</span>
                        </div>
                      )}
                    </div>

                    {/* Çıkış Fotoğrafı - Kod deaktive olduğunda ve giriş fotoğrafı varsa */}
                    <div className={`rounded-lg p-3 border ${canTakeCheckOutPhoto() && !checkOutPhoto ? 'bg-red-900/30 border-red-600' : 'bg-gray-800/50 border-gray-700'}`}>
                      <p className="text-xs font-medium text-red-300 mb-2 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" />
                        </svg>
                        Çıkış
                      </p>
                      
                      {checkOutPhoto ? (
                        <div className="relative">
                          <img 
                            src={checkOutPhoto} 
                            alt="Çıkış" 
                            className="w-full h-20 object-cover rounded"
                          />
                          <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                            ✓
                          </div>
                        </div>
                      ) : canTakeCheckOutPhoto() ? (
                        <>
                          <input
                            ref={checkOutInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handlePhotoCapture(e, 'checkOut')}
                            className="hidden"
                          />
                          <button
                            onClick={() => checkOutInputRef.current?.click()}
                            disabled={uploading.checkOut}
                            className="w-full py-3 rounded border-2 border-dashed border-red-500 hover:bg-red-900/30 transition-all"
                          >
                            {uploading.checkOut ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-red-500 mx-auto"></div>
                            ) : (
                              <div className="text-center">
                                <svg className="w-6 h-6 mx-auto text-red-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-xs text-red-400">Çek</span>
                              </div>
                            )}
                          </button>
                        </>
                      ) : (
                        <div className="py-3 text-center text-gray-500">
                          <svg className="w-6 h-6 mx-auto opacity-50 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-xs">{!checkInPhoto ? 'Önce giriş' : 'Süre dolunca'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="mt-2 text-xs text-gray-400">
                    <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Giriş fotoğrafı kod aktifken, çıkış fotoğrafı süre dolunca çekilmelidir.
                  </p>
                </div>
              )}
              
              <p className="text-sm text-blue-300 mt-3">
                <svg className="w-5 h-5 inline-block mr-1 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Bu kod sadece rezervasyon saatleriniz içinde çalışır. Lütfen başkalarıyla paylaşmayınız.
              </p>
            </>
          ) : (
            <div className="text-center py-6 slide-up">
              <svg className="w-16 h-16 text-blue-400 opacity-50 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-blue-200">
                Bugün için aktif bir rezervasyonunuz bulunmamaktadır.
              </p>
            </div>
          )}
        </div>
        
        {pin && (
          <div className="ml-4">
            <Button
              onClick={toggleVisibility}
              variant="secondary"
              className="text-sm hover-scale"
            >
              {isVisible ? (
                <>
                  <span>Gizle</span>
                  <svg className="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </>
              ) : (
                <>
                  <span>Göster</span>
                  <svg className="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PinDisplay;
